// src/pages/admin/AdminDashboard.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Drawer,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  AttachMoney,
  Assignment,
  CheckCircle,
  Schedule,
  Receipt,
  Star,
  Business,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import '../../css/admin/admin.css';

import {
  useGetDashboardAnalyticsQuery,
  RangeType,
} from '../../features/admin/dashboardApi.ts';

const RANGE_OPTIONS: { value: RangeType; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom' },
];

const formatNumber = (value?: number) =>
  typeof value === 'number' ? value.toLocaleString('en-IN') : '0';

const formatCurrency = (value?: number) => `${formatNumber(value)} AED`;

const calcPercent = (current?: number, last?: number): number | null => {
  if (last === undefined || last === 0 || current === undefined) return null;
  return ((current - last) / last) * 100;
};

const AdminDashboard: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rangeType, setRangeType] = useState<RangeType>('last_week');

  const { data, error, isLoading, isFetching } =
    useGetDashboardAnalyticsQuery(rangeType);

  const handleToggleMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRangeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setRangeType(event.target.value as RangeType);
  };

  // Safely read values from API
  const dailyTotalSales = data?.daily_sales?.total?.sales ?? 0;
  const dailyTotalOrders = data?.daily_sales?.total?.orders ?? 0;

  const weeklyNewUsersCurrent = data?.new_users?.total?.current_week ?? 0;
  const weeklyNewUsersLast = data?.new_users?.total?.last_week ?? 0;
  const weeklyNewUsersDiff = weeklyNewUsersCurrent - weeklyNewUsersLast;
  const weeklyNewUsersPercent = calcPercent(
    weeklyNewUsersCurrent,
    weeklyNewUsersLast
  );

  const rangeSalesCurrent = data?.range?.range_summary?.total?.sales ?? 0;
  const rangeSalesPercent =
    data?.range?.comparison?.total?.sales?.percent ?? null;

  const monthCurrent = data?.month_growth?.total?.current ?? 0;
  const monthLast = data?.month_growth?.total?.last ?? 0;
  const monthPercent = calcPercent(monthCurrent, monthLast);

  const quarterCurrent = data?.quarter_growth?.total?.current ?? 0;
  const quarterLast = data?.quarter_growth?.total?.last ?? 0;
  const quarterPercent = calcPercent(quarterCurrent, quarterLast);

  // 4 top stat cards (now dynamic)
  const statsCards = [
    {
      title: "Today's Total Sales",
      value: formatCurrency(dailyTotalSales),
      icon: <Assignment />,
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      progress: 100,
    },
    {
      title: "Today's Total Orders",
      value: formatNumber(dailyTotalOrders),
      icon: <CheckCircle />,
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      progress: 100,
    },
    {
      title: 'New Users (This Week)',
      value: formatNumber(weeklyNewUsersCurrent),
      icon: <Schedule />,
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      progress: 100,
    },
    {
      title: 'Change vs Last Week (Users)',
      value: `${
        weeklyNewUsersPercent !== null
          ? weeklyNewUsersPercent.toFixed(1)
          : '0'
      }%`,
      icon: <Receipt />,
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      progress: Math.min(
        100,
        Math.max(
          0,
          weeklyNewUsersPercent !== null
            ? Math.abs(weeklyNewUsersPercent)
            : 0
        )
      ),
    },
  ];

  // 3 sales cards (dynamic)
  const salesCards = [
    {
      title: 'Current Range Sales',
      value: formatCurrency(rangeSalesCurrent),
      subtitle: 'Selected period total sales',
      icon: <TrendingUp />,
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      change:
        rangeSalesPercent !== null
          ? `${rangeSalesPercent.toFixed(1)}%`
          : '0%',
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(monthCurrent),
      subtitle: `Last Month: ${formatCurrency(monthLast)}`,
      icon: <AttachMoney />,
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      change:
        monthPercent !== null ? `${monthPercent.toFixed(1)}%` : '0%',
    },
    {
      title: 'Quarter Revenue',
      value: formatCurrency(quarterCurrent),
      subtitle: `Last Quarter: ${formatCurrency(quarterLast)}`,
      icon: <ShoppingCart />,
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      change:
        quarterPercent !== null
          ? `${quarterPercent.toFixed(1)}%`
          : '0%',
    },
  ];

  const topProducts =
    data?.top_products?.map((p, index) => ({
      name: `Product #${p.product_id}`,
      quantity: p.total_quantity,
      // just show "up" for all; you can computer trend if backend adds it later
      trend: 'up' as 'up' | 'down',
      rank: index + 1,
    })) ?? [];

  // Top vendors still demo data (no vendors in API)
  const topVendors = [
    { name: 'SS Medi Care Ltd.', sales: 10, rating: 4.8 },
    { name: 'ABC Ltd.', sales: 9, rating: 4.7 },
    { name: 'Grocery Solutions', sales: 8, rating: 4.6 },
    { name: 'MedTech Corp.', sales: 7, rating: 4.5 },
    { name: 'HealthCare Plus', sales: 6, rating: 4.4 },
  ];

  const currentRangeLabel =
    RANGE_OPTIONS.find((r) => r.value === rangeType)?.label || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Row: Sidebar + Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box
          sx={{
            width: '250px',
            flexShrink: 0,
            overflowY: 'auto',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Slidemenu
            mobileOpen={false}
            onToggle={function (): void {
              throw new Error('Function not implemented.');
            }}
          />
        </Box>

        {/* Mobile Sidebar */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleToggleMenu}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu
            mobileOpen={false}
            onToggle={function (): void {
              throw new Error('Function not implemented.');
            }}
          />
        </Drawer>

        {/* Main Content area with Header + Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ flexShrink: 0 }}>
            <AdminHeader onToggleMenu={handleToggleMenu} />
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              padding: { xs: 1, sm: 2 },
              paddingTop: '10px',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              minHeight: '100vh',
            }}
          >
            <Box
              sx={{
                padding: { xs: 1, sm: 3 },
                maxWidth: '1400px',
                margin: '0 auto',
              }}
            >
              {/* Dashboard Header + Range Filter */}
              <Box
                sx={{
                  mb: 4,
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  padding: 3,
                  color: 'white',
                  boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: '28px', sm: '36px' },
                      mb: 1,
                    }}
                  >
                    📊 Admin Dashboard
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {`Overview for: ${currentRangeLabel}`}
                  </Typography>
                </Box>

                <FormControl
                  size="small"
                  sx={{ minWidth: 200, backgroundColor: 'white', borderRadius: 2 }}
                >
                  <InputLabel id="range-type-label">Range</InputLabel>
                  <Select
                    labelId="range-type-label"
                    value={rangeType}
                    label="Range"
                    onChange={handleRangeChange}
                  >
                    {RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Loading / Error State */}
              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 5,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {error && !isLoading && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="error">
                    Failed to load dashboard analytics.
                  </Alert>
                </Box>
              )}

              {!isLoading && (
                <>
                  {isFetching && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress />
                    </Box>
                  )}

                  {/* Stats Cards Row (4) */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {statsCards.map((card, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                          sx={{
                            background: card.gradient,
                            color: 'white',
                            borderRadius: 3,
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  mr: 2,
                                  width: 50,
                                  height: 50,
                                }}
                              >
                                {card.icon}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="h4"
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  {card.value}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ opacity: 0.9 }}
                                >
                                  {card.title}
                                </Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={card.progress}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: 'rgba(255,255,255,0.8)',
                                },
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Sales Cards Row (3) */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {salesCards.map((card, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <Card
                          sx={{
                            background: card.gradient,
                            color: 'white',
                            borderRadius: 3,
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            height: '100%',
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                            },
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 3,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  mr: 2,
                                  width: 45,
                                  height: 45,
                                }}
                              >
                                {card.icon}
                              </Avatar>
                              <Chip
                                label={card.change}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            </Box>
                            <Typography
                              variant="h3"
                              sx={{ fontWeight: 'bold', mb: 1 }}
                            >
                              {card.value}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ opacity: 0.9, mb: 1 }}
                            >
                              {card.title}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              {card.subtitle}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Analytics Row */}
                  
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
